import { Stack, styled } from '@mui/material'
import { ColumnDef, flexRender, getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table'
import { MonetaryCell, MonetaryValueDataObjectType, tokens } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza/text'

type Period = {
    date: string
    // Each row in the DCF table is of a generic category, and can hold either Monetary Values or Numbers
    value?: MonetaryValueDataObjectType | number
}

type CategoryDCF = 'market_rent_cashflow' | 'revenue_cashflow' | 'cost_cashflows' | 'net_cashflow' | 'discount_factor' | 'present_value'

export type RowDCF = {
    category: CategoryDCF
    subcategory?: string
    periods: Period[]
    exitPeriod: Period
    subRows?: RowDCF[]
    isTotal?: boolean
}

export type DCFTableProps = {
    columns: ColumnDef<RowDCF>[]
    data: RowDCF[]
    terminalValue: MonetaryValueDataObjectType
    presentValue: MonetaryValueDataObjectType
    presentValueWithTransactionCost?: MonetaryValueDataObjectType
    assetValueRounded: MonetaryValueDataObjectType
    copy?: DCFTableCopy
}

export type DCFTableCopy = {
    columnLabels: {
        period: (period: number) => string
        exit: string
    }
    sections: {
        marketRent: string
        revenue: string
        costs: string
        presentValueCalculation: string
    }
    rows: {
        grossRevenue: string
        totalCosts: string
        discountFactor: string
        totalNetRevenue: string
        presentValue: string
        terminalValue: string
        presentValueTotal: string
        presentValueAfterTransactionCosts: string
        rounded: string
    }
}

export const DEFAULT_DCF_TABLE_COPY: DCFTableCopy = {
    columnLabels: {
        period: (period) => `Periode ${period}`,
        exit: 'Exit',
    },
    sections: {
        marketRent: 'Marktmiete',
        revenue: 'Erträge',
        costs: 'Kosten',
        presentValueCalculation: 'Barwertberechnung',
    },
    rows: {
        grossRevenue: 'Bruttoertrag',
        totalCosts: 'Gesamtkosten',
        discountFactor: 'Diskontierungsfaktor',
        totalNetRevenue: 'Total Nettoertrag',
        presentValue: 'Barwert',
        terminalValue: 'Endwert/Ewige Rente',
        presentValueTotal: 'Summe Barwert',
        presentValueAfterTransactionCosts: 'Summe Barwert abzügl. Erwerbsnebenkosten',
        rounded: 'Gerundet',
    },
}

export function TableDCF({
    columns,
    data,
    terminalValue,
    presentValue,
    presentValueWithTransactionCost,
    assetValueRounded,
    copy = DEFAULT_DCF_TABLE_COPY,
}: DCFTableProps) {
    const table = useReactTable({
        columns,
        data,
        getCoreRowModel: getCoreRowModel(),
        getSubRows: (row) => row.subRows,
        getExpandedRowModel: getExpandedRowModel(),
    })

    const getRows = (category: CategoryDCF) => table.getRowModel().rows.filter((row) => row.original.category === category)
    const getRow = (category: CategoryDCF) => table.getRowModel().rows.find((row) => row.original.category === category)

    const marketRentRows = getRows('market_rent_cashflow')
    const revenueRows = getRows('revenue_cashflow')
    const costRows = getRows('cost_cashflows')
    const netRow = getRow('net_cashflow')
    const discountFactorRow = getRow('discount_factor')
    const presentValueRow = getRow('present_value')

    const numberOfColumns = table.getAllColumns().length

    const getTableCellVariant = (index: number, isExpanded?: boolean, isTotalRow?: boolean) => {
        if (isExpanded) {
            return 'expanded'
        }
        if (index === numberOfColumns - 1) {
            return 'exit'
        }
        if (isTotalRow) {
            return 'total'
        }
        return undefined
    }

    const getFontWeight = (index: number, isTotalRow?: boolean) => {
        if (isTotalRow) {
            if (index === 0) {
                return 'medium'
            }
            return 'heavy'
        }
        return undefined
    }

    return (
        <StyledTable $numberOfColumns={numberOfColumns}>
            <StyledSectionOfRows $withoutBorders>
                <StyledRow>
                    {table.getFlatHeaders().map((header, index) => (
                        <StyledCell key={header.id} $variant={getTableCellVariant(index)}>
                            {index !== 0 && (
                                <Stack direction="column" alignItems="center" width="100%">
                                    <Typography variant="headline5" noWrap>
                                        {index === numberOfColumns - 1 ? copy.columnLabels.exit : copy.columnLabels.period(index)}
                                    </Typography>
                                    <Typography variant="headline5" color={tokens.ColorTextSubtle}>
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                    </Typography>
                                </Stack>
                            )}
                        </StyledCell>
                    ))}
                </StyledRow>
            </StyledSectionOfRows>

            <StyledSectionOfRows title={copy.sections.marketRent}>
                {marketRentRows.map((row) => {
                    const isExpanded = row.getParentRow()?.getIsExpanded()
                    const isTotalRow = row.original.isTotal
                    return (
                        <StyledRow key={row.id}>
                            {row.getVisibleCells().map((cell, index) => (
                                <StyledCell
                                    key={cell.id}
                                    $variant={getTableCellVariant(index, isExpanded, isTotalRow)}
                                    $fontWeight={getFontWeight(index, isTotalRow)}
                                >
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </StyledCell>
                            ))}
                        </StyledRow>
                    )
                })}
            </StyledSectionOfRows>

            <StyledSectionOfSections>
                <StyledSectionOfRows $isInnerSection title={copy.sections.revenue}>
                    {revenueRows.map((row) => {
                        const isExpanded = row.getParentRow()?.getIsExpanded()
                        const isTotalRow = row.original.isTotal
                        return (
                            <StyledRow key={row.id}>
                                {row.getVisibleCells().map((cell, index) => (
                                    <StyledCell
                                        key={cell.id}
                                        $variant={getTableCellVariant(index, isExpanded, isTotalRow)}
                                        $fontWeight={getFontWeight(index, isTotalRow)}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </StyledCell>
                                ))}
                            </StyledRow>
                        )
                    })}
                </StyledSectionOfRows>
                <StyledSectionOfRows $isInnerSection title={copy.sections.costs}>
                    {costRows.map((row) => {
                        const isExpanded = row.getParentRow()?.getIsExpanded()
                        const isTotalRow = row.original.isTotal
                        return (
                            <StyledRow key={row.id}>
                                {row.getVisibleCells().map((cell, index) => (
                                    <StyledCell
                                        key={cell.id}
                                        $variant={getTableCellVariant(index, isExpanded, isTotalRow)}
                                        $fontWeight={getFontWeight(index, isTotalRow)}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </StyledCell>
                                ))}
                            </StyledRow>
                        )
                    })}
                </StyledSectionOfRows>
                <StyledSectionOfRows $isInnerSection $withoutBorders>
                    <StyledRow>
                        {netRow?.getVisibleCells().map((cell, index) => (
                            <StyledCell
                                key={cell.id}
                                $withHighlight
                                $fontWeight={index === 0 ? 'medium' : 'heavy'}
                                $variant={getTableCellVariant(index, false, true)}
                            >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </StyledCell>
                        ))}
                    </StyledRow>
                    <StyledRow>
                        <StyledSummationCell>{copy.rows.terminalValue}</StyledSummationCell>
                        <StyledCell $fontWeight="heavy" $variant="exit">
                            <MonetaryCell monetaryValue={terminalValue} />
                        </StyledCell>
                    </StyledRow>
                </StyledSectionOfRows>
            </StyledSectionOfSections>

            <StyledSectionOfRows title={copy.sections.presentValueCalculation}>
                <StyledRow>
                    {discountFactorRow?.getVisibleCells().map((cell, index) => (
                        <StyledCell key={cell.id} $variant={getTableCellVariant(index)}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </StyledCell>
                    ))}
                </StyledRow>
                <StyledRow>
                    {presentValueRow?.getVisibleCells().map((cell, index) => (
                        <StyledCell
                            key={cell.id}
                            $fontWeight={index === 0 ? 'medium' : 'heavy'}
                            $variant={getTableCellVariant(index, false, true)}
                        >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </StyledCell>
                    ))}
                </StyledRow>
                <StyledRow>
                    <StyledSummationCell $isTotal $withHighlight>
                        {copy.rows.presentValueTotal}
                    </StyledSummationCell>
                    <StyledCell $variant="total" $withHighlight $fontWeight="heavy">
                        <MonetaryCell monetaryValue={presentValue} />
                    </StyledCell>
                </StyledRow>
                <StyledRow>
                    <StyledSummationCell>{copy.rows.presentValueAfterTransactionCosts}</StyledSummationCell>
                    <StyledCell $fontWeight="heavy">
                        <MonetaryCell monetaryValue={presentValueWithTransactionCost} />
                    </StyledCell>
                </StyledRow>
                <StyledRow>
                    <StyledSummationCell $isTotal $withHighlight>
                        {copy.rows.rounded}
                    </StyledSummationCell>
                    <StyledCell $variant="total" $withHighlight $fontWeight="heavy">
                        <MonetaryCell monetaryValue={assetValueRounded} />
                    </StyledCell>
                </StyledRow>
            </StyledSectionOfRows>
        </StyledTable>
    )
}

const SUBGRID_STYLING = {
    display: 'grid',
    gridTemplateColumns: 'subgrid',
    gridColumn: '1 / -1',
}

const StyledTable = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $numberOfColumns: number }>(({ $numberOfColumns }) => ({
    display: 'grid',
    rowGap: tokens.SizingSpacing200,
    gridTemplateColumns: `repeat(${$numberOfColumns}, minmax(min-content, 1fr))`,
    overflowX: 'scroll',
    position: 'relative',
    padding: '1px',
}))

const StyledSectionOfSections = styled('div')(() => ({
    ...SUBGRID_STYLING,
    rowGap: tokens.SizingSpacing200,
    padding: `${tokens.SizingSpacing150} ${tokens.SizingSpacing100}`,
    borderRadius: tokens.SizingCornerM,
    outline: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
}))

const StyledSectionOfRows = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $isInnerSection?: boolean; $withoutBorders?: boolean }>(({ $isInnerSection, $withoutBorders }) => ({
    ...SUBGRID_STYLING,
    padding: $isInnerSection
        ? `${!$withoutBorders ? tokens.SizingSpacing100 : 0} ${tokens.SizingSpacing100}`
        : `${!$withoutBorders ? tokens.SizingSpacing150 : 0} ${tokens.SizingSpacing200}`,
    borderRadius: tokens.SizingCornerM,
    outline: !$withoutBorders ? `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}` : undefined,
    '&::after': {
        content: 'attr(title)',
        position: 'absolute',
        padding: `0 ${tokens.SizingSpacing075}`,
        backgroundColor: tokens.ColorSurfaceDefault,
        ...tokens.Headline5,
        color: tokens.ColorTextSubtle,
        marginTop: $isInnerSection ? '-17px' : '-21px',
    },
}))

const StyledRow = styled('div')(() => ({
    ...SUBGRID_STYLING,
}))

type CellStylingProps = { $variant?: 'exit' | 'total' | 'expanded'; $withHighlight?: boolean; $fontWeight?: 'medium' | 'heavy' }

const COMMON_CELL_STYLING = ({ $variant, $withHighlight, $fontWeight }: CellStylingProps) => ({
    padding: `${tokens.SizingSpacing150} ${tokens.SizingSpacing100}`,
    ...tokens.TextXs,
    borderTop: $withHighlight ? `${tokens.SizingBorderS} solid ${tokens.ColorBorderSelectedDefault}` : undefined,
    ...($fontWeight !== undefined ? { fontWeight: $fontWeight === 'medium' ? 500 : 600 } : {}),
    backgroundColor:
        $variant === 'expanded'
            ? 'rgba(242, 242, 242, 0.50)' // TODO: Replace with an actual token value once it exists
            : $variant === 'exit'
              ? tokens.ColorSurfaceAccentRoyalDefault
              : $variant === 'total'
                ? tokens.ColorSurfaceAccentGreenDefault
                : undefined,
})

const StyledCell = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<CellStylingProps>(({ $variant, $withHighlight, $fontWeight }) => ({
    ...COMMON_CELL_STYLING({ $variant, $withHighlight, $fontWeight }),
}))

const StyledSummationCell = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $isTotal?: boolean; $withHighlight?: boolean }>(({ $isTotal, $withHighlight }) => ({
    gridColumn: '1 / -2',
    marginLeft: 'auto',
    width: '320px',
    boxSizing: 'border-box',
    ...COMMON_CELL_STYLING({ $variant: $isTotal ? 'total' : undefined, $withHighlight, $fontWeight: 'medium' }),
}))
